<template lang='pug'>
.job
	map-viewer.job-map(
		:pin='jobLocation'
		:location='jobLocation'
		v-if='job.location.latitude && job.location.longitude'
	)

	.job-info(v-if='job')
		.header
			p.sub-title {{ job.jobsType.description }}
			h1.title.place {{ job.name }}
			p.date {{ dateFormat( job.dateCreated ) }}

		.icon-container
			p.facility.icon-water(v-if='job.facility') Facility {{ job.facility.name }} No.{{ job.facility.number }} ({{ job.facility.facilitiesKey }})
			p.manager.icon-person-outlined Managed by {{ job.manager }}
			p.location.icon-pin-point {{ job.location ? job.location.description : 'Location Unknown' }}
			p.funded.icon-money(v-if='job.fund') Funded by {{ job.fund.description }}

		.button-container
			button-expand(
				title='Update Status'

				:options='statusOptions'

				@optionClicked='updateStatus'
			)

			.expand-info(@click='setExpandState')
				p Expand all Info
				.clickable-arrow(:class='expandState')

	.info
		list-layout(
			:layout='listItems'
			:parentObj='formattedJob'
			:class='{ "slide-in" : expandState === "open" }'
		)
</template>

<script>
import MapViewer from '@/components/map/Viewer';
import ButtonExpand from '@/components/ButtonExpand';
import ListLayout from '@/components/ListLayout';

import Submit from '@/lib/Submit';
import { objectOrNull } from '@/lib/vue-validators';
import { FormatDate } from '@/lib/utils';
import { JobStatuses } from '@/lib/assets/jobs';

export default {
	name : 'job',

	props : {
		job : {
			required  : true,
			validator : objectOrNull,
		}
	},

	data : () => ( {
		expandState : 'close',
	} ),

	computed : {

		formattedJob() {

			let {
				projectFee,
				fidCost,
				fidEstimate,
				devEstimate,
			} = this.job;

			const addDollarSign = ( fee ) => {

				if ( !fee && fee !== 0 ) {
					return null;
				}

				if ( fee[0] === '$' ) {
					return fee;
				}

				return `$${fee}`;

			};

			projectFee  = addDollarSign( projectFee );
			fidCost     = addDollarSign( fidCost );
			fidEstimate = addDollarSign( fidEstimate );
			devEstimate = addDollarSign( devEstimate );

			return {
				...this.job,
				projectFee,
				fidCost,
				fidEstimate,
				devEstimate,
			};

		},

		listItems() {
			return {
				title : 'General Information',
				items : {
					driveLink   : 'Drive Link',
					name        : 'Name',
					number      : 'Number',
					tractNumber : 'Tract Number',
					facilities  : {
						items : {
							name : 'Facilities',
						}
					},
					manager : 'Manager',
					fund    : {
						items : {
							description : 'Fund',
						}
					},
					expenseType : 'Expense Type',
					category    : 'Category',
					type        : 'Type',
					status      : 'Status',
					firm        : {
						items : {
							description : 'Firm',
						}
					},
					projectFee : 'Project Fee',
					fidCost    : 'FID Cost',
					summary    : 'Summary',
					location   : {
						items : {
							description : 'Cross Streets',
						}
					},
					actionRequests : {
						items : {
							number : 'Action Requests'
						},
					},

					dates : {
						title  : 'Dates',
						parent : this.formattedJob,
						items  : {
							dateCreated : 'Date Created',
							dateClosed  : 'Date Closed',
							dateStarted : 'Date Started',
							dateEnded   : 'Date Ended',
						}
					},

					materials : {
						title  : 'Materials',
						parent : this.formattedJob,
						items  : {
							pipeLength   : 'Pipe Length',
							pipeDiameter : 'Pipe Diameter',
							pipeType     : {
								items : {
									description : 'Pipe Type',
								}
							},
							fidEstimate : 'FID Estimate',
							devEstimate : 'Dev Estimate',
						}
					},
				}
			};
		},

		jobLocation() {
			if ( !this.job ) {
				return undefined;
			}

			if ( !this.job.location ) {
				return undefined;
			}

			const { latitude, longitude } = this.job.location;

			return {
				lat : latitude,
				lng : longitude,
			};
		},

		statusOptions() {

			let options = JobStatuses.map( ( status ) => {
				status.active = false; // eslint-disable-line
				return status;
			} );

			const { status }   = this.job;
			const statusClosed = ( status === 'Complete' || status === 'Closed' );

			if ( !this.job.dateClosed && ( !statusClosed ) ) {
				options = options.filter( ( { value } ) => ( value !== 'Complete' && value !== 'Closed' ) );
			}

			if ( this.job.status ) {
				const currentStatus = options.find( a => a.value === this.job.status );

				currentStatus.active = true;

				return options;
			}

			return options;
		},
	},

	methods : {

		setExpandState() {
			const { expandState } = this;
			this.expandState = expandState === 'open' ? 'close' : 'open';
		},

		dateFormat( date ) {
			return FormatDate( date, 'MMMM D, YYYY', 'GMT' );
		},

		updateStatus( value ) {

			const changedJob = {
				value : {
					status : value.value,
					type   : this.job.type,
				}
			};

			const { type } = changedJob.value;

			this.$store.dispatch( 'loadWithDisplayState', {
				options : {
					message        : 'Updating status...',
					failMessage    : 'Failed to update status',
					successMessage : 'Updated status',
					state          : 'loading',
				},
				method : () => Submit.editJob( changedJob, type, this.job.jobsKey ),
			} );

		},

	},

	components : {
		MapViewer,
		ButtonExpand,
		ListLayout,
	}
};
</script>

<style lang='scss'>

.job {
	display: flex;
	flex-flow: row wrap;
	width: 100%;

	.job-map {
		flex: 0 0 30%;
		border-radius: 0 15px 15px 0;

		.map {
			height: 250px;
		}

		+ .job-info {
			padding: 0;
			padding-left: 20px;
		}
	}

	.job-info {
		flex: 1 1 70%;
		display: flex;
		flex-flow: column;
		align-items: flex-start;
		justify-content: space-between;
		padding: 0 30px;

		.header {
			display: flex;
			flex-flow: column;
			align-items: flex-start;
			margin-top: 10px;

			.sub-title {
				color: $color-primary;
				font-size: 14px;
				font-weight: 300;
				margin-bottom: 0;
			}

			.title.place {
				color: $blueGrey;
				font-size: 24px;
				font-weight: 500;
				margin-bottom: 0;
			}

			.date {
				color: $blueGrey;
				font-size: 14px;
			}
		}

		.icon-container {
			display: grid;
			grid-template-columns: 47.5% 47.5%;
			grid-column-gap: 5%;
			grid-row-gap: 10px;
			align-items: center;
			width: 100%;
			margin: 15px 0;

			.facility.icon-water {
				flex-flow: row nowrap;
			}

			p {
				font-family: 'Poppins', sans-serif!important;
				display: flex;
				align-items: center;
				font-size: 16px;
				font-weight: 500;
				line-height: 130%;

				&::before {
					font-family: 'icomoon';
					margin-right: 10px;
					font-size: 18px;
				}
			}
		}

		.button-container {
			display: flex;
			align-items: center;
			justify-content: flex-start;
			margin: 10px 0;

			.expand-container {
				margin-right: 30px;

				.button {
					box-shadow: none;
				}

				.option {
					font-weight: 500;
					font-size: 14px;
					color: #50606E;
				}
			}

			a.icon-export {
				font-family: 'Poppins', sans-serif!important;
				cursor: pointer;
				color: $input-color;

				&::before {
					font-family: 'icomoon';
					float: right;
					margin-left: 15px;
				}
			}

			.expand-info {
				margin-left: 30px;

				p {
					color: $input-color;
				}

				.clickable-arrow {

					&::before,
					&::after {
						background-color: $input-color !important;
					}
				}
			}
		}
	}

	.info {

		> .list-layout {
			padding: 0 30px;
		}

		.item-fund,
		.item-pipeType {
			flex: 2 2 48%;
		}
	}
}

@media only screen and ( max-width: 640px ) {

	.job {

		.job-map {
			flex: 0 0 100%;
			border-radius: 0 !important;

			+ .job-info {
				padding: 30px 30px 0 30px;
			}
		}

		.job-info {

			.icon-container {
				grid-template-columns: auto;
			}

			.button-container {

				.expand-container {
					display: none;
				}

				.expand-info {
					margin-left: 0;
				}
			}
		}

		.info {

			> .list-layout {

				.section {

					.list-item {
						flex: 0 0 100%;

						&.item-expenseType,
						&.item-category,
						&.item-type,
						&.item-manager,
						&.item-number,
						&.item-status,
						&.item-firm {
							flex: 0 0 50%;
						}
					}
				}
			}
		}
	}
}
</style>
